import styled from '@emotion/styled'
import CssBaseline from '@mui/material/CssBaseline'
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles'
import { ToastAlertProvider } from '@design-systems/canvas-ui'
import { lazy, Suspense } from 'react'
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom'
import { isPartnerApplicationEnabled } from '../../appConfig/applyConfig'
import Header from '../../components/Header/header'
import Home from '../Home'
import { flag } from '../../flag/index'
import './app.css'

const StyledContainer = styled('div')({
  minWidth: '300px',
})

// Lazy-loaded components
const DocsLazy = lazy(() => import('../../components/Docs'))
const PartnerApplicationLazy = lazy(
  () => import('../../components/PartnerApplication'),
)
const PartnerNavigationLazy = lazy(
  () => import('../../components/PartnerNavigation'),
)
const PartnerApplicationSubmittedLazy = lazy(
  () =>
    import('../../components/PartnerNavigation/PartnerApplicationSubmitted'),
)
const HowItWorksPageLazy = lazy(() => import('../../components/HowItWorksPage'))
const ReferralRatesLazy = lazy(() => import('../../components/ReferralRates'))
const ChannelPartnerAgenciesLazy = lazy(
  () => import('../../components/ChannelPartnerAgencies'),
)
const ServiceOfferingsLazy = lazy(
  () => import('../../components/ServiceOfferings'),
)
const SuccessStoriesLazy = lazy(() => import('../../components/SuccessStories'))
const CaseStudyDetailLazy = lazy(
  () => import('../../components/SuccessStories/CaseStudyDetail'),
)
const EventsLazy = lazy(() => import('../../components/Events'))

const PartnerApplicationSuccessRoute = () => {
  const location = useLocation()
  const hasSessionMarker =
    sessionStorage.getItem('tplus_submission_success') === 'true'

  if (!location.state?.fromSubmission && !hasSessionMarker) {
    return <Navigate to="/apply/v2" replace />
  }

  return (
    <Suspense fallback={<div className="suspenseFallback" />}>
      <PartnerApplicationSubmittedLazy />
    </Suspense>
  )
}

const theme = createTheme({
  typography: {
    fontFamily: [
      'Helvetica for Target',
      'HelveticaforTargetRegular',
      'HelveticaforTargetBold',
      'TargetHelvitica',
    ].join(','),
  },
})

export const App = () => {
  const isTplusRevampFlagEnabled = flag('TPLUS_REVAMP')
  const isTplusLeadFormV2FlagEnabled = flag('TPLUS_LEAD_FORM_V2')
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline>
          <ToastAlertProvider location="top" className="appToastStack">
            <StyledContainer>
              <Router>
                <Header />
                <Routes>
                  <Route exact path="/" element={<Home />} />
                  <Route path="/docs/:type/:subtype" element={<DocsLazy />} />
                  {isPartnerApplicationEnabled && (
                    <Route
                      path="/apply"
                      element={
                        <Suspense
                          fallback={<div className="suspenseFallback" />}
                        >
                          <PartnerApplicationLazy />
                        </Suspense>
                      }
                    />
                  )}
                  {isTplusLeadFormV2FlagEnabled && (
                    <>
                      <Route
                        path="/apply/v2"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <PartnerNavigationLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/apply/v2/success"
                        element={<PartnerApplicationSuccessRoute />}
                      />
                    </>
                  )}
                  {isTplusRevampFlagEnabled && (
                    <>
                      <Route
                        path="/referral-rates"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <ReferralRatesLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/how-it-works"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <HowItWorksPageLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/service-offerings"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <ServiceOfferingsLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/service-providers"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <ChannelPartnerAgenciesLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/success-stories"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <SuccessStoriesLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/success-stories/:id"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <CaseStudyDetailLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/events"
                        element={
                          <Suspense
                            fallback={<div className="suspenseFallback" />}
                          >
                            <EventsLazy />
                          </Suspense>
                        }
                      />
                    </>
                  )}
                </Routes>
              </Router>
            </StyledContainer>
            <div id="canvasPortalContainer" />
          </ToastAlertProvider>
        </CssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default App
